import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useRenderStore, useAuthStore } from "@/store";
import axios from "axios";
import {
  ArrowLeft,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Instagram,
  Facebook,
  Youtube,
  Trash2,
  Pencil,
} from "lucide-react";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import { translateCurrentPathText as t } from "@/lib/translations";

export default function RenderPage() {
  const { renderId } = useParams();
  const navigate = useLocalizedNavigation();
  const { currentRender, fetchRender, pollRenderStatus, deleteRender } = useRenderStore();
  const { checkAuth } = useAuthStore();
  const [isPolling, setIsPolling] = useState(false);
  const [socialConnections, setSocialConnections] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [sharingPlatform, setSharingPlatform] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(null);
  const [shareError, setShareError] = useState(null);
  const [shareTitle, setShareTitle] = useState("");
  const [shareCaption, setShareCaption] = useState("");
  const [youtubePrivacy, setYoutubePrivacy] = useState("public");
  const [selectedFacebookPageIndex, setSelectedFacebookPageIndex] = useState(0);
  const [showFacebookPageSelector, setShowFacebookPageSelector] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const displayProgress = currentRender?.status === "completed"
    ? 100
    : Math.max(0, Math.min(100, Math.round(currentRender?.progress || 0)));

  useEffect(() => {
    if (renderId) {
      fetchRender(renderId);
      fetchSocialConnections();
    }
  }, [renderId, fetchRender]);

  const fetchSocialConnections = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "/social/connections",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSocialConnections(response.data);
    } catch (error) {
      console.error("Failed to fetch social connections:", error);
    }
  };

  const openOAuthPopup = (oauthUrl, popupName = "SocialOAuth") => {
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      oauthUrl,
      popupName,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      throw new Error("popup_blocked");
    }

    const popupClosedInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(popupClosedInterval);
        fetchSocialConnections();
      }
    }, 500);
  };

  const handleConnectInstagram = async () => {
    setIsConnecting(true);
    setConnectingPlatform("instagram");
    setShareError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "/auth/instagram/login",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      openOAuthPopup(response.data.oauth_url, "InstagramOAuth");
    } catch (error) {
      console.error("Failed to initiate Instagram connection:", error);
      setShareError(
        error.message === "popup_blocked"
          ? "Popup blocked. Please allow popups and try again."
          : "Failed to connect Instagram. Please try again."
      );
    } finally {
      setIsConnecting(false);
      setConnectingPlatform(null);
    }
  };


  useEffect(() => {
    const facebookConnection = getFacebookConnection();
    const pages = facebookConnection?.pages || [];
    if (!pages.length) {
      setSelectedFacebookPageIndex(0);
      setShowFacebookPageSelector(false);
      return;
    }

    if (selectedFacebookPageIndex >= pages.length) {
      setSelectedFacebookPageIndex(0);
    }

    if (pages.length <= 1) {
      setShowFacebookPageSelector(false);
    }
  }, [socialConnections, selectedFacebookPageIndex]);

  const handleConnectFacebook = async () => {
    setIsConnecting(true);
    setConnectingPlatform("facebook");
    setShareError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/auth/facebook/login", {
        headers: { Authorization: `Bearer ${token}` },
      });
      openOAuthPopup(response.data.oauth_url, "FacebookOAuth");
    } catch (error) {
      console.error("Failed to initiate Facebook connection:", error);
      setShareError(
        error.message === "popup_blocked"
          ? "Popup blocked. Please allow popups and try again."
          : error.response?.data?.detail || "Failed to connect Facebook. Please try again."
      );
    } finally {
      setIsConnecting(false);
      setConnectingPlatform(null);
    }
  };

  const handleConnectYoutube = async () => {
    setIsConnecting(true);
    setConnectingPlatform("youtube");
    setShareError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/auth/youtube/login", {
        headers: { Authorization: `Bearer ${token}` },
      });
      openOAuthPopup(response.data.oauth_url, "YoutubeOAuth");
    } catch (error) {
      console.error("Failed to initiate YouTube connection:", error);
      setShareError(
        error.message === "popup_blocked"
          ? "Popup blocked. Please allow popups and try again."
          : error.response?.data?.detail || "Failed to connect YouTube. Please try again."
      );
    } finally {
      setIsConnecting(false);
      setConnectingPlatform(null);
    }
  };

  const handleConnectTiktok = async () => {
    setIsConnecting(true);
    setConnectingPlatform("tiktok");
    setShareError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/auth/tiktok/login", {
        headers: { Authorization: `Bearer ${token}` },
      });
      openOAuthPopup(response.data.oauth_url, "TikTokOAuth");
    } catch (error) {
      console.error("Failed to initiate TikTok connection:", error);
      setShareError(
        error.message === "popup_blocked"
          ? "Popup blocked. Please allow popups and try again."
          : error.response?.data?.detail || "Failed to connect TikTok. Please try again."
      );
    } finally {
      setIsConnecting(false);
      setConnectingPlatform(null);
    }
  };

  useEffect(() => {
    const handleMetaOauthMessage = (event) => {
      if (event.data?.type !== "social-oauth") {
        return;
      }

      const platform = event.data.platform || "social";
      if (event.data.status === "success") {
        setShareSuccess(`${platform.charAt(0).toUpperCase() + platform.slice(1)} account connected successfully.`);
        setShareError(null);
      } else if (event.data.status === "error") {
        setShareError(event.data.message || `${platform} connection failed.`);
        setShareSuccess(null);
      }

      fetchSocialConnections();
    };

    window.addEventListener("message", handleMetaOauthMessage);
    return () => {
      window.removeEventListener("message", handleMetaOauthMessage);
    };
  }, []);

  const handleShareToInstagram = async () => {
    setIsSharing(true);
    setSharingPlatform("instagram");
    setShareError(null);
    setShareSuccess(null);
    
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("render_id", renderId);
      formData.append("caption", shareCaption.trim() || "Created with CreaVideos");
      formData.append("ig_account_index", "0");

      const response = await axios.post(
        "/social/share/instagram",
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setShareSuccess(`Successfully shared to @${response.data.username}!`);
    } catch (error) {
      console.error("Failed to share to Instagram:", error);
      setShareError(
        error.response?.data?.detail || "Failed to share to Instagram. Please try again."
      );
    } finally {
      setIsSharing(false);
      setSharingPlatform(null);
    }
  };

  const handleShareToFacebook = async () => {
    setIsSharing(true);
    setSharingPlatform("facebook");
    setShareError(null);
    setShareSuccess(null);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("render_id", renderId);
      formData.append("title", shareTitle.trim());
      formData.append("caption", shareCaption.trim());
      formData.append("page_index", String(selectedFacebookPageIndex));

      const response = await axios.post("/social/share/facebook", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShareSuccess(`Successfully shared to Facebook page ${response.data.page_name || ""}`.trim());
    } catch (error) {
      setShareError(error.response?.data?.detail || "Failed to share to Facebook. Please try again.");
    } finally {
      setIsSharing(false);
      setSharingPlatform(null);
    }
  };

  const handleShareToYoutube = async () => {
    setIsSharing(true);
    setSharingPlatform("youtube");
    setShareError(null);
    setShareSuccess(null);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("render_id", renderId);
      formData.append("title", shareTitle.trim() || `CreaVideos render ${renderId?.slice(0, 8)}`);
      formData.append("description", shareCaption.trim());
      formData.append("privacy_status", youtubePrivacy);

      const response = await axios.post("/social/share/youtube", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShareSuccess(`YouTube upload started/finished for video ID ${response.data.media_id || "unknown"}.`);
    } catch (error) {
      setShareError(error.response?.data?.detail || "Failed to share to YouTube. Please try again.");
    } finally {
      setIsSharing(false);
      setSharingPlatform(null);
    }
  };

  const handleShareToTikTok = async () => {
    setIsSharing(true);
    setSharingPlatform("tiktok");
    setShareError(null);
    setShareSuccess(null);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("render_id", renderId);
      formData.append("title", shareCaption.trim() || "Created with CreaVideos");
      const response = await axios.post("/social/share/tiktok", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShareSuccess(response.data.message || "Video sent to TikTok.");
    } catch (error) {
      setShareError(error.response?.data?.detail || "Failed to share to TikTok. Please try again.");
    } finally {
      setIsSharing(false);
      setSharingPlatform(null);
    }
  };

  const handleShareToAllPlatforms = async () => {
    const connectedPlatforms = [];
    if (getInstagramConnection()) connectedPlatforms.push("instagram");
    if (getFacebookConnection()) connectedPlatforms.push("facebook");
    if (getYoutubeConnection()) connectedPlatforms.push("youtube");
    if (getTiktokConnection()) connectedPlatforms.push("tiktok");

    if (connectedPlatforms.length === 0) {
      setShareError(t("Please connect at least one platform before sharing."));
      setShareSuccess(null);
      return;
    }

    setIsSharing(true);
    setSharingPlatform("all");
    setShareError(null);
    setShareSuccess(null);

    const token = localStorage.getItem("token");
    const successes = [];
    const failures = [];

    const shareRequests = {
      instagram: async () => {
        const formData = new FormData();
        formData.append("render_id", renderId);
        formData.append("caption", shareCaption.trim() || "Created with CreaVideos");
        formData.append("ig_account_index", "0");
        await axios.post("/social/share/instagram", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      },
      facebook: async () => {
        const formData = new FormData();
        formData.append("render_id", renderId);
        formData.append("title", shareTitle.trim());
        formData.append("caption", shareCaption.trim());
        formData.append("page_index", String(selectedFacebookPageIndex));
        await axios.post("/social/share/facebook", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      },
      youtube: async () => {
        const formData = new FormData();
        formData.append("render_id", renderId);
        formData.append("title", shareTitle.trim() || `CreaVideos render ${renderId?.slice(0, 8)}`);
        formData.append("description", shareCaption.trim());
        formData.append("privacy_status", youtubePrivacy);
        await axios.post("/social/share/youtube", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      },
      tiktok: async () => {
        const formData = new FormData();
        formData.append("render_id", renderId);
        formData.append("title", shareCaption.trim() || "Created with CreaVideos");
        await axios.post("/social/share/tiktok", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      },
    };

    for (const platform of connectedPlatforms) {
      try {
        await shareRequests[platform]();
        successes.push(platform);
      } catch (error) {
        failures.push(platform);
      }
    }

    if (successes.length > 0) {
      setShareSuccess(
        `${t("Shared successfully to")}: ${successes.map((platform) => platform.charAt(0).toUpperCase() + platform.slice(1)).join(", ")}.`
      );
    }

    if (failures.length > 0) {
      setShareError(
        `${t("Failed to share to")}: ${failures.map((platform) => platform.charAt(0).toUpperCase() + platform.slice(1)).join(", ")}.`
      );
    }

    setIsSharing(false);
    setSharingPlatform(null);
  };

  const getInstagramConnection = () => {
    return socialConnections.find((conn) => conn.platform === "instagram");
  };
  const getFacebookConnection = () => {
    return socialConnections.find((conn) => conn.platform === "facebook");
  };
  const getFacebookPages = () => getFacebookConnection()?.pages || [];
  const getSelectedFacebookPage = () => getFacebookPages()[selectedFacebookPageIndex] || getFacebookPages()[0];
  const getYoutubeConnection = () => socialConnections.find((conn) => conn.platform === "youtube");
  const getTiktokConnection = () => socialConnections.find((conn) => conn.platform === "tiktok");

  const handleDisconnect = async (platform) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/social/connections/${platform}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShareSuccess("Account disconnected.");
      setShareError(null);
      fetchSocialConnections();
    } catch (error) {
      setShareError(error.response?.data?.detail || "Failed to disconnect account.");
    }
  };


  const handleDeleteRender = async () => {
    if (!currentRender?.id || isDeleting) return;

    const confirmed = window.confirm("Delete this render? This action cannot be undone.");
    if (!confirmed) return;

    setIsDeleting(true);
    const success = await deleteRender(currentRender.id);
    setIsDeleting(false);

    if (success) {
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    if (currentRender && (currentRender.status === "queued" || currentRender.status === "processing") && !isPolling) {
      setIsPolling(true);
      pollRenderStatus(renderId).then(() => setIsPolling(false));
    }
  }, [currentRender, renderId, pollRenderStatus, isPolling]);

  useEffect(() => {
    if (currentRender?.status === "completed") {
      checkAuth();
    }
  }, [currentRender?.status, checkAuth]);

  const getStatusIcon = () => {
    switch (currentRender?.status) {
      case "completed":
        return <CheckCircle className="w-12 h-12 text-green-500" />;
      case "failed":
        return <XCircle className="w-12 h-12 text-red-500" />;
      case "processing":
        return <Loader2 className="w-12 h-12 text-violet-500 animate-spin" />;
      default:
        return <Clock className="w-12 h-12 text-slate-400" />;
    }
  };

  const getStatusMessage = () => {
    switch (currentRender?.status) {
      case "completed":
        return t("Your video is ready!");
      case "failed":
        return "Rendering failed. Please try again.";
      case "processing":
        return t("Processing your video...");
      case "queued":
        return "Your video is queued for processing...";
      default:
        return "Unknown status";
    }
  };

  if (!currentRender) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-semibold">Render Status</h1>
            <p className="text-sm text-slate-500">ID: {renderId?.slice(0, 8)}...</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          {/* Status Icon */}
          <div className="mb-6">{getStatusIcon()}</div>

          {/* Status Message */}
          <h2 className="text-2xl font-bold mb-2">{getStatusMessage()}</h2>
          <p className="text-slate-600 mb-6">
            {currentRender.status === "processing" && `${t("Progress:")} ${displayProgress}%`}
            {currentRender.status === "failed" && currentRender.error}
          </p>

          {/* Progress Bar */}
          {(currentRender.status === "processing" || currentRender.status === "queued") && (
            <div className="max-w-md mx-auto mb-8">
              <Progress value={displayProgress} className="h-3" />
              <p className="text-sm text-slate-500 mt-2">
                {t("This may take a few minutes depending on video length and complexity")}
              </p>
            </div>
          )}

          {/* Video Preview */}
          {currentRender.status === "completed" && currentRender.output_url && (
            <div className="mb-8">
              <div className="max-w-md mx-auto rounded-xl overflow-hidden bg-black">
                <video
                  src={currentRender.output_url}
                  controls
                  preload="metadata"
                  className="w-full"
                  poster=""
                  data-testid="render-video"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {currentRender.status === "completed" && currentRender.output_url && (
              <>
                <Button
                  className="btn-primary rounded-full"
                  asChild
                  data-testid="download-btn"
                >
                  <a 
                    href={currentRender.output_url}
                    download={`creavideos_${currentRender.id}.mp4`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Video
                  </a>
                </Button>
                {currentRender.editor_snapshot?.template && (
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() => navigate(`/editor/${currentRender.template_id}?renderId=${currentRender.id}`)}
                    data-testid="modify-video-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    {t("Modify Video")}
                  </Button>
                )}
              </>
            )}

            {currentRender.status === "failed" && (
              <Button
                className="btn-primary rounded-full"
                onClick={() => navigate(`/editor/${currentRender.template_id}`)}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}

            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </Button>

            <Button
              variant="outline"
              className="rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={handleDeleteRender}
              disabled={isDeleting}
            >
              {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </div>

          {currentRender.status === "completed" && currentRender.output_url && (
            <div className="mt-8 border-t border-slate-200 pt-6 text-left">
              <h3 className="text-lg font-semibold mb-4">{t("Share to social media")}</h3>

              <div className="space-y-3">

                <div className="flex flex-col md:flex-row gap-3">
                  <input
                    type="text"
                    data-testid="youtube-title"
                    placeholder={t("Title (for Youtube only)")}
                    value={shareTitle}
                    onChange={(e) => setShareTitle(e.target.value)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  />
                  <input
                    type="text"
                    placeholder={t("Caption/Description (for all the platforms)")}
                    value={shareCaption}
                    onChange={(e) => setShareCaption(e.target.value)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div className="space-y-3">
                  <Button
                    className="btn-primary rounded-full w-full md:w-auto"
                    data-testid="share-all-platforms" onClick={handleShareToAllPlatforms}
                    disabled={isSharing || isConnecting}
                  >
                    {isSharing && sharingPlatform === "all" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : null}
                    {isSharing && sharingPlatform === "all" ? t("Sharing...") : t("Share to all platforms")}
                  </Button>

                  <p className="text-sm text-slate-600 leading-relaxed">
                    {t("Connect your social accounts first using the buttons below, then click Share to all platforms to publish automatically everywhere you're connected. You can connect one, two, or all platforms, and still use each platform's individual Share button after connecting. For TikTok, the video is sent to your account and you must finish posting inside the TikTok app or website.")}
                  </p>
                </div>

                <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                  <div className="flex flex-col gap-2 md:w-56">
                    {getInstagramConnection() ? (
                      <>
                        <Button
                          className="btn-primary rounded-full"
                          onClick={handleShareToInstagram}
                          disabled={isSharing && sharingPlatform !== "instagram"}
                        >
                          {isSharing && sharingPlatform === "instagram" ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Instagram className="w-4 h-4 mr-2" />
                          )}
                          {isSharing && sharingPlatform === "instagram" ? "Sharing..." : "Share to Instagram"}
                        </Button>
                        <div className="text-xs text-slate-600">
                          Connected as {getInstagramConnection()?.username || getInstagramConnection()?.instagram_accounts?.[0]?.username || "Instagram account"}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => handleDisconnect(getInstagramConnection()?.platform || "instagram")}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full hover:from-purple-600 hover:to-pink-600"
                        onClick={handleConnectInstagram}
                        disabled={isConnecting}
                      >
                        {isConnecting && connectingPlatform === "instagram" ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Instagram className="w-4 h-4 mr-2" />
                        )}
                        {isConnecting && connectingPlatform === "instagram" ? "Connecting..." : "Connect Instagram"}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                  <div className="flex flex-col gap-2 md:w-56">
                    {getFacebookConnection() ? (
                      <>
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={handleShareToFacebook}
                          disabled={isSharing && sharingPlatform !== "facebook"}
                        >
                          {isSharing && sharingPlatform === "facebook" ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Facebook className="w-4 h-4 mr-2" />
                          )}
                          {isSharing && sharingPlatform === "facebook" ? "Sharing..." : "Share to Facebook"}
                        </Button>
                        <div className="text-xs text-slate-600 space-y-1">
                          <div>
                            Connected as {getFacebookConnection()?.facebook_user_name || "Facebook account"}
                          </div>
                          <div>
                            to page: {getSelectedFacebookPage()?.page_name || "Facebook Page"}
                          </div>
                        </div>
                        {getFacebookPages().length > 1 ? (
                          <div className="space-y-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full"
                              onClick={() => setShowFacebookPageSelector((value) => !value)}
                            >
                              Select page
                            </Button>
                            {showFacebookPageSelector ? (
                              <select
                                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                                value={selectedFacebookPageIndex}
                                onChange={(event) => {
                                  setSelectedFacebookPageIndex(Number(event.target.value));
                                  setShowFacebookPageSelector(false);
                                }}
                              >
                                {getFacebookPages().map((page, index) => (
                                  <option key={page.page_id || index} value={index}>
                                    {page.page_name || `Facebook Page ${index + 1}`}
                                  </option>
                                ))}
                              </select>
                            ) : null}
                          </div>
                        ) : null}
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => handleDisconnect(getFacebookConnection()?.platform || "facebook")}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={handleConnectFacebook}
                        disabled={isConnecting}
                      >
                        {isConnecting && connectingPlatform === "facebook" ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Facebook className="w-4 h-4 mr-2" />
                        )}
                        {isConnecting && connectingPlatform === "facebook" ? "Connecting..." : "Connect Facebook"}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                  <div className="flex flex-col gap-2 md:w-56">
                    {getYoutubeConnection() ? (
                      <>
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={handleShareToYoutube}
                          disabled={isSharing && sharingPlatform !== "youtube"}
                        >
                          {isSharing && sharingPlatform === "youtube" ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Youtube className="w-4 h-4 mr-2" />
                          )}
                          {isSharing && sharingPlatform === "youtube" ? "Sharing..." : "Share to YouTube"}
                        </Button>
                <label className="block text-sm">
                  YouTube — {t("Privacy")}
                  <select data-testid="youtube-privacy" value={youtubePrivacy}
                    onChange={(event) => setYoutubePrivacy(event.target.value)}
                    className="ml-3 rounded border border-slate-300 px-3 py-2">
                    <option value="public">{t("Public")}</option>
                    <option value="unlisted">{t("Unlisted")}</option>
                    <option value="private">{t("Private")}</option>
                  </select>
                </label>
                        <div className="text-xs text-slate-600">
                          Connected as {getYoutubeConnection()?.channel_title || "YouTube channel"}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => handleDisconnect("youtube")}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={handleConnectYoutube}
                        disabled={isConnecting}
                      >
                        {isConnecting && connectingPlatform === "youtube" ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Youtube className="w-4 h-4 mr-2" />
                        )}
                        {isConnecting && connectingPlatform === "youtube" ? "Connecting..." : "Connect YouTube"}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                  <div className="flex flex-col gap-2 md:w-56">
                    {getTiktokConnection() ? (
                      <>
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={handleShareToTikTok}
                          disabled={isSharing && sharingPlatform !== "tiktok"}
                        >
                          {isSharing && sharingPlatform === "tiktok" ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : null}
                          {isSharing && sharingPlatform === "tiktok" ? "Sharing..." : "Share to TikTok"}
                        </Button>
                        <div className="text-xs text-slate-600">
                          Connected as {getTiktokConnection()?.open_id || "TikTok account"}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => handleDisconnect("tiktok")}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={handleConnectTiktok}
                        disabled={isConnecting}
                      >
                        {isConnecting && connectingPlatform === "tiktok" ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : null}
                        {isConnecting && connectingPlatform === "tiktok" ? "Connecting..." : "Connect TikTok"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Share Status Messages */}
          {shareSuccess && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
              <CheckCircle className="w-5 h-5 inline mr-2" />
              {shareSuccess}
            </div>
          )}

          {shareError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              <XCircle className="w-5 h-5 inline mr-2" />
              {shareError}
            </div>
          )}

          {/* Render Details */}
          <div className="mt-8 pt-8 border-t border-slate-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Created</p>
                <p className="font-medium">
                  {new Date(currentRender.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Status</p>
                <p className="font-medium capitalize">{currentRender.status}</p>
              </div>
              <div>
                <p className="text-slate-500">Template</p>
                <p className="font-medium truncate">{currentRender.template_id?.slice(0, 8)}...</p>
              </div>
              <div>
                <p className="text-slate-500">Progress</p>
                <p className="font-medium">{currentRender.progress || 0}%</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
